import React from 'react';
import { PageName, Language } from '../../../types/pageBuilder';
import usePageSections from '../../../hooks/usePageSections';
import SectionList from './SectionList';

interface PageEditorProps {
  pageName: PageName;
  language: Language;
}

/**
 * Main page editor component
 * Manages sections for a specific page
 */
const PageEditor: React.FC<PageEditorProps> = ({ pageName, language }) => {
  const {
    sections,
    loading,
    error,
    refetch,
    reorderSections,
    toggleVisibility,
    deleteSection
  } = usePageSections({
    pageName,
    autoFetch: true
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading sections...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 text-red-600 mt-0.5 mr-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="flex-1">
              <h3 className="text-red-800 font-medium">Error Loading Sections</h3>
              <p className="text-red-600 text-sm mt-1">{error}</p>
              <button
                onClick={refetch}
                className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Page Info */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 capitalize">{pageName} Page</h2>
        <p className="text-gray-600 mt-1">
          Manage sections for the {pageName} page. Drag to reorder, toggle visibility, or edit content.
        </p>
      </div>

      {/* Section List */}
      <SectionList
        sections={sections}
        language={language}
        onReorder={reorderSections}
        onToggleVisibility={toggleVisibility}
        onDelete={deleteSection}
        onRefresh={refetch}
      />

      {/* Info Box */}
      {sections.length > 0 && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 text-blue-600 mt-0.5 mr-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="text-sm text-blue-800">
              <p className="font-medium">Tips:</p>
              <ul className="mt-2 space-y-1 list-disc list-inside">
                <li>Drag sections to reorder them</li>
                <li>Click the eye icon to show/hide sections</li>
                <li>Click the edit icon to modify section content</li>
                <li>Sections marked as "Dynamic" pull content from other database tables</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageEditor;
